import type {
	EffectRegion,
	FieldDefect,
	MetamorphopsiaState,
	ScaleRegion,
} from "@client/types/vision";
import type { DistortionPoint, ScotomaSpot } from "shared/schema/src/types";
import { applyBrightnessToImageData } from "./brightness";
import { applyColorShiftToImageData } from "./colorShift";
import { applyContrastToImageData } from "./contrast";
import { applyFieldDefectBlackout } from "./fieldDefect";
import { applyExtremeBlur, applyGaussianBlurToImageData } from "./gaussianBlur";
import { applyMeshDistortionToImageData } from "./meshDistortion";
import { applyMetamorphopsiaToImageData } from "./metamorphopsia";
import { applyRegionScale } from "./regionScale";
import { applyRotationToImageData } from "./rotation";
import { applyScotomaToImageData } from "./scotoma";
import { applyVacuumEffectToImageData } from "./vacuum";

/**
 * Apply multiple image processing filters in a single pass
 * This reduces GPU readbacks from N filters → 1 readback for significant performance gains
 *
 * @param imageData - Source image data
 * @param filters - Filter configurations
 * @param disabledFilters - Set of disabled filter keys
 * @returns Processed ImageData
 */
export function applyPreDiploPipeline(
	imageData: ImageData,
	filters: {
		rotation?: { angle: number };
		colourShift?: {
			hue: number;
			saturation: number;
			preset?: "Cyanopsia" | "Xanthopsia";
		};
		contrast?: {
			level: number;
			roiLevel?: number;
			mode?: "grayscale" | "warm" | "cool";
			effectRegion?: EffectRegion | null;
		};
		imageBrightness?: {
			value: number;
			roiValue?: number;
			effectRegion?: EffectRegion | null;
		};
		scotoma?: ScotomaSpot[];
		blurriness?: {
			sigma: number;
			roiSigma?: number;
			effectRegion?: EffectRegion | null;
		};
		fieldDefect?: FieldDefect | null;
		regionScale?: {
			region: ScaleRegion;
			scale: number;
			bridge: number;
		} | null;
	},
	disabledFilters: Set<string>,
): ImageData {
	let result = imageData;

	// Apply rotation
	if (
		!disabledFilters.has("rotation") &&
		filters.rotation &&
		filters.rotation.angle !== 0
	) {
		result = applyRotationToImageData(result, filters.rotation.angle);
	}

	// Apply color shift
	if (
		!disabledFilters.has("colourShift") &&
		filters.colourShift &&
		(filters.colourShift.hue !== 0 ||
			filters.colourShift.saturation !== 1 ||
			filters.colourShift.preset)
	) {
		result = applyColorShiftToImageData(
			result,
			filters.colourShift.hue,
			filters.colourShift.saturation,
			filters.colourShift.preset,
		);
	}

	// Apply contrast.
	// Compat mode for legacy users: when a region exists and roiLevel is at
	// its default (1), interpret as "apply base to region only" (the pre-F6
	// behavior). When roiLevel != 1, use the F6 dual-pass (base on whole
	// image plus boost in region) for AMD-style simulation.
	if (!disabledFilters.has("contrast") && filters.contrast) {
		const { level = 1, roiLevel = 1, mode, effectRegion } = filters.contrast;
		const hasBaseEffect = level !== 1 || !!mode;
		const wantsBoost = roiLevel !== 1;
		if (effectRegion && !wantsBoost && hasBaseEffect) {
			// Legacy region-only: scope base to the region.
			result = applyContrastToImageData(result, level, mode, effectRegion);
		} else {
			if (hasBaseEffect) {
				result = applyContrastToImageData(result, level, mode, null);
			}
			if (effectRegion && wantsBoost) {
				if (level === 0) {
					// Symmetric to the brightness divide-by-zero branch below.
					result = applyContrastToImageData(
						result,
						roiLevel,
						undefined,
						effectRegion,
					);
				} else {
					const correction = roiLevel / level;
					result = applyContrastToImageData(
						result,
						correction,
						undefined,
						effectRegion,
					);
				}
			}
		}
	}

	// Apply brightness.
	// Compat mode for legacy users: when a region exists and roiValue is at
	// its default (1), interpret as "apply base to region only". When
	// roiValue != 1, use the F6 dual-pass for AMD-style simulation.
	if (
		!disabledFilters.has("imageBrightness") &&
		filters.imageBrightness !== undefined
	) {
		const { value = 1, roiValue = 1, effectRegion } = filters.imageBrightness;
		const hasBaseEffect = value !== 1;
		const wantsBoost = roiValue !== 1;
		const baseClamped = Math.max(0.1, Math.min(2.0, value));
		if (effectRegion && !wantsBoost && hasBaseEffect) {
			// Legacy region-only: scope base to the region.
			result = applyBrightnessToImageData(result, baseClamped, effectRegion);
		} else {
			if (hasBaseEffect) {
				result = applyBrightnessToImageData(result, baseClamped, null);
			}
			if (effectRegion && wantsBoost) {
				if (value === 0) {
					result = applyBrightnessToImageData(result, roiValue, effectRegion);
				} else {
					const correction = roiValue / value;
					result = applyBrightnessToImageData(result, correction, effectRegion);
				}
			}
		}
	}

	// Apply scotoma
	if (
		!disabledFilters.has("scotoma") &&
		filters.scotoma &&
		filters.scotoma.length > 0
	) {
		result = applyScotomaToImageData(result, filters.scotoma);
	}

	// Apply field defect (pitch-black curtain / tunnel) on top of the face.
	if (!disabledFilters.has("fieldDefect") && filters.fieldDefect) {
		result = applyFieldDefectBlackout(result, filters.fieldDefect);
	}

	// F10: region scale (regional micropsia/macropsia). Shares the "anisoconia"
	// disabled key; the caller only passes this in region mode, so whole-head
	// and region are mutually exclusive at render time.
	if (
		!disabledFilters.has("anisoconia") &&
		filters.regionScale &&
		filters.regionScale.scale !== 1
	) {
		result = applyRegionScale(
			result,
			filters.regionScale.region,
			filters.regionScale.scale,
			filters.regionScale.bridge,
		);
	}

	// Apply blur: base pass on whole image, then optional ROI boost. The tier
	// ladder (standard / moderate-extreme / heavy-extreme) runs for each pass.
	// Sequential Gaussians at sigma A then sigma B compose to effective sigma
	// sqrt(A^2 + B^2), not A+B; clinically acceptable per F6 spec edge case 3.
	if (!disabledFilters.has("blurriness") && filters.blurriness) {
		const { sigma = 0, roiSigma = 0, effectRegion } = filters.blurriness;

		const runBlurTiers = (s: number, region: EffectRegion | null) => {
			if (s <= 0) return;
			if (s <= 2.0) {
				const blurAmount = Math.round((1 + s * 0.8) * 8);
				result = applyGaussianBlurToImageData(result, blurAmount, region);
			} else if (s <= 5.0) {
				result = applyGaussianBlurToImageData(result, 20, region);
				const t = (s - 2.0) / 3.0;
				const scaleFactor = Math.round(2 + t * 6);
				result = applyExtremeBlur(result, scaleFactor, 10, region);
			} else {
				const t = (s - 5.0) / 5.0;
				const gaussianRadius = Math.round(20 + t * 10);
				result = applyGaussianBlurToImageData(result, gaussianRadius, region);
				const scaleFactor = Math.round(8 + t * 24);
				result = applyExtremeBlur(result, scaleFactor, 10, region);
			}
		};

		// Compat mode for legacy users: when a region exists and roiSigma is 0
		// (default), interpret as "blur only inside the region" (pre-F6
		// behavior). When roiSigma > 0, take the F6 dual-pass: base on whole
		// image plus additional blur in region.
		const wantsBoost = roiSigma > 0;
		if (effectRegion && !wantsBoost && sigma > 0) {
			runBlurTiers(sigma, effectRegion);
		} else {
			runBlurTiers(sigma, null);
			if (effectRegion && wantsBoost) {
				runBlurTiers(roiSigma, effectRegion);
			}
		}
	}

	return result;
}

/**
 * Apply distortion and vacuum effects in a single pass
 * These must be applied after the grid is drawn so the grid gets distorted
 *
 * @param imageData - Source image data (with grid already drawn)
 * @param filters - Filter configurations
 * @param disabledFilters - Set of disabled filter keys
 * @returns Processed ImageData
 */
export function applyPostGridPipeline(
	imageData: ImageData,
	filters: {
		metamorphopsia?: MetamorphopsiaState;
		distortion?: {
			points: Array<{
				original: DistortionPoint;
				current: DistortionPoint;
			}>;
			power: number;
			range: number;
		};
		vacuum?: {
			points: Array<{
				original: DistortionPoint;
				current: DistortionPoint;
			}>;
			power: number;
			range: number;
		};
	},
	disabledFilters: Set<string>,
): ImageData {
	let result = imageData;

	if (
		!disabledFilters.has("metamorphopsia") &&
		filters.metamorphopsia?.enabled
	) {
		result = applyMetamorphopsiaToImageData(result, filters.metamorphopsia);
	}

	// Apply mesh distortion
	if (
		!disabledFilters.has("distortion") &&
		filters.distortion &&
		filters.distortion.points.length > 0
	) {
		result = applyMeshDistortionToImageData(
			result,
			filters.distortion.points,
			20,
			filters.distortion.power,
			filters.distortion.range,
		);
	}

	// Apply vacuum effect
	if (
		!disabledFilters.has("vacuum") &&
		filters.vacuum &&
		filters.vacuum.points.length > 0
	) {
		result = applyVacuumEffectToImageData(
			result,
			filters.vacuum.points,
			filters.vacuum.power,
			filters.vacuum.range,
		);
	}

	return result;
}
